/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-get, no-restricted-properties */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import EmberObject, { action, computed } from '@ember/object';
import { alias, mapBy } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import PageType from 'linced-app/enums/page-type';
import moment from 'moment';
import * as arrays from '@linc-technologies/linced-common/utils/arrays';
import {
  findBy,
  mapBy as mapByArray,
  sortBy,
} from '@linc-technologies/linced-common/utils/arrays';

export default class PageController extends Controller {
  @service
  session;

  @service
  terms;

  @alias('model.groups')
  allGroups;

  @alias('model.learners')
  allLearners;

  @alias('terms.currentTerm')
  currentTerm;

  @alias('session.school.jurisdiction')
  jurisdiction;

  @alias('session.school')
  school;

  @tracked
  detailDialogOpen = false;

  // Filter props
  @tracked from = null;

  @tracked to = null;

  // todo: add more genders for when MoE supports them
  genders = Object.freeze([
    { label: 'Male', value: 'M' },
    { label: 'Female', value: 'F' },
  ]);

  // Filters
  @tracked excludedTags = [];

  @tracked includedTags = [];
  @tracked selectedEthnicities = [];
  @tracked selectedGroups = [];
  @tracked
  selectedLearners = [];
  @tracked
  excludedLearners = [];
  @tracked selectedYearLevels = [];
  @tracked
  selectedGenders = [];
  @tracked
  selectedATSI = [];

  // Computed
  @computed('model.page.type')
  get isLearnerPage() {
    return this.get('model.page.type') === PageType.STUDENT;
  }

  @computed('filteredPosts.@each.person_id')
  get countData() {
    let byPerson = this.filteredPosts.reduce((counts, post) => {
      if (isEmpty(post.person_id) || isEmpty(post.person)) {
        // Nobody to count this post against - skip it rather than blowing up.
        return counts;
      }

      let data = findBy(counts, 'personId', post.person_id);
      if (isEmpty(data)) {
        counts.push({
          personId: post.person_id,
          person: post.person,
          count: 0,
        });
        data = findBy(counts, 'personId', post.person_id);
      }

      data.count++;
      return counts;
    }, []);

    return sortBy(
      byPerson.reduce((counts, data) => {
        let count = findBy(counts, 'count', data.count);
        if (isEmpty(count)) {
          counts.push({
            count: data.count,
            people: [],
          });
          count = findBy(counts, 'count', data.count);
        }

        count.people.push(data.person.get('fullName'));
        return counts;
      }, []),
      'count',
    ).reverse();
  }

  get dates() {
    return {
      startDate: moment.unix(this.from),
      endDate: moment.unix(this.to),
    };
  }

  @computed(
    'excludedTags',
    'includedTags',
    'miscFilters',
    'selectedEthnicities',
    'selectedGroups',
    'selectedLearners.[]',
    'excludedLearners.[]',
    'selectedYearLevels',
    'selectedGenders.[]',
    'selectedATSI.[]',
  )
  get filters() {
    return EmberObject.create({
      excludedTags: this.excludedTags,
      includedTags: this.includedTags,
      selectedEthnicities: this.selectedEthnicities,
      selectedGroups: this.selectedGroups,
      selectedLearners: this.selectedLearners,
      excludedLearners: this.excludedLearners,
      selectedYearLevels: this.selectedYearLevels,
      selectedGenders: this.selectedGenders,
      selectedATSI: this.selectedATSI,
    });
  }

  // postMetaPeople provides an intermediary CP so the @each dependent key below
  // stays one level deep.
  @mapBy('model.postMeta', 'person')
  postMetaPeople;

  @tracked
  detailCategory;

  @tracked
  detailTag;

  @computed(
    'filteredStudents.[]',
    'selectedLearners.@each.id',
    'excludedLearners.@each.id',
    'model.postMeta.[]',
    'postMetaPeople.@each.id',
    'includedTags.[]',
    'excludedTags.[]',
    'isLearnerPage',
  )
  get filteredPosts() {
    let allPosts = this.get('model.postMeta');

    if (!this.isLearnerPage) {
      // Ony learner pages have extensive filtering, return all posts
      return allPosts;
    }

    if (isEmpty(this.filteredStudents)) {
      // No students, no posts
      return [];
    }

    // Filter by students and tags
    let selectedPersonIds = new Set(mapByArray(this.selectedLearners, 'id'));
    let excludedPersonIds = new Set(mapByArray(this.excludedLearners, 'id'));
    let filteredStudentIds = new Set(mapByArray(this.filteredStudents, 'id'));
    let includedTagIds = mapByArray(this.includedTags, 'id');
    let excludedTagIds = mapByArray(this.excludedTags, 'id');

    allPosts = allPosts.filter((post) => {
      let personId = post.get('person.id');

      // Check excluded persons
      if (excludedPersonIds.has(personId)) {
        return false;
      }

      // Check selected persons
      if (!isEmpty(selectedPersonIds) && !selectedPersonIds.has(personId)) {
        return false;
      }

      // Check filtered students
      if (!filteredStudentIds.has(personId)) {
        return false;
      }

      // Check tags
      // tags is an async hasMany; its PromiseManyArray has no toArray(), and
      // the ids are all that's needed.
      let postTagIds = new Set(post.hasMany('tagIds').ids());
      // Exclude post if it has any excluded tags.
      if (excludedTagIds.some((id) => postTagIds.has(id))) {
        return false;
      }

      // Exclude post if it's missing any included tags.
      return !includedTagIds.some((id) => !postTagIds.has(id));
    });

    return allPosts;
  }

  @computed(
    'allLearners.[]',
    'selectedGroups.[]',
    'selectedYearLevels.[]',
    'selectedEthnicities.[]',
    'selectedGenders.[]',
    'selectedATSI.[]',
  )
  get filteredStudents() {
    let allStudents = this.allLearners;

    // Filter by the group - top-level filter, goes first
    if (this.selectedGroups.length > 0) {
      const selectedGroupsLearners = mapByArray(
        this.selectedGroups,
        'sortedCurrentLearners',
      ).flat();
      allStudents = arrays.uniq(arrays.mapBy(selectedGroupsLearners, 'person'));
    }

    // Filter by lower-level filters in step 3
    let selectedYearLevels = this.selectedYearLevels;
    let selectedGenders = mapByArray(this.selectedGenders, 'value');
    let selectedEthnicities = mapByArray(this.selectedEthnicities, 'value');
    let selectedATSI = mapByArray(this.selectedATSI, 'label');

    if (
      selectedYearLevels.length > 0 ||
      selectedGenders.length > 0 ||
      selectedEthnicities.length > 0 ||
      selectedATSI.length > 0
    ) {
      allStudents = allStudents.filter((student) => {
        let matchesYearLevel = true;
        let matchesGender = true;
        let matchesEthnicity = true;
        let matchesATSI = true;

        if (selectedYearLevels.length > 0) {
          matchesYearLevel = selectedYearLevels.includes(
            String(student.currentYearLevel),
          );
        }

        if (selectedGenders.length > 0) {
          matchesGender = selectedGenders.includes(student.gender);
        }

        if (selectedEthnicities.length > 0) {
          matchesEthnicity = selectedEthnicities.some((selectedEthnicity) =>
            student.allEthnicities.includes(selectedEthnicity),
          );
        }

        if (selectedATSI.length > 0) {
          matchesATSI = selectedATSI.includes(student.atsi);
        }

        return (
          matchesYearLevel && matchesGender && matchesEthnicity && matchesATSI
        );
      });
    }
    return allStudents;
  }

  @computed('filteredPosts.@each.author_id', 'model.tagCategories.[]')
  get tagData() {
    return this.filteredPosts
      .filter((post) => post.tags?.length)
      .reduce((categories, post) => {
        post.tags.forEach((tag) => {
          let category = categories.find(
            (item) => item.categoryId === tag.get('category.id'),
          );
          if (isEmpty(category)) {
            categories.push({
              categoryId: tag.get('category.id'),
              category: tag.category,
              tags: [],
              numTags: 0,
            });
            category = categories.find(
              (item) => item.categoryId === tag.get('category.id'),
            );
          }
          category.numTags++;

          let tagData = findBy(category.tags, 'id', tag.id);
          if (isEmpty(tagData)) {
            category.tags.push({
              id: tag.id,
              label: tag.label,
              count: 0,
              people: [],
            });
            tagData = findBy(category.tags, 'id', tag.id);
          }
          tagData.count++;

          if (!findBy(tagData.people, 'id', post.get('person.id'))) {
            tagData.people.push(post.person);
          }
        });

        return categories;
      }, []);
  }

  @computed('model.postAuthors.[]', 'filteredPosts.@each.{id,date}')
  get timelineData() {
    if (isEmpty(this.model.postAuthors)) {
      return [];
    }
    return this.filteredPosts.reduce((rows, post) => {
      let postId = post.get('id');
      let authorData = this.model.postAuthors[postId];

      if (isEmpty(authorData)) {
        // Skip if authorData is not available
        return rows;
      }

      let authorId = authorData.authorId;
      switch (authorData.type) {
        case PersonType.LEARNER:
          authorId = 'learner';
          break;
        case PersonType.CAREGIVER:
          authorId = 'caregiver';
          break;
      }

      let row = findBy(rows, 'authorId', authorId);
      if (isEmpty(row)) {
        let label = authorData.fullName;
        switch (authorId) {
          case 'learner':
            label = '[Students]';
            break;
          case 'caregiver':
            label = '[Caregivers]';
            break;
        }

        row = {
          authorId,
          label,
          times: [],
        };
        rows.push(row);
      }

      row.times.push({
        start: post.date * 1000,
        end: (post.date + 86400) * 1000,
      });

      return rows;
    }, []);
  }

  // tags is an async hasMany, so read the loaded records from tagsArray.
  @computed('model.page.tagCategories.@each.tagsArray')
  get pageTags() {
    const tags = new Set();
    for (const category of this.get('model.page.tagCategories') ?? []) {
      for (const tag of category.tagsArray) {
        tags.add(tag);
      }
    }
    return [...tags];
  }

  @action
  reset() {
    // Default to 01.01 this year in unix
    this.from = moment().startOf('year').format('X');
    // Default to end of today in unix
    this.to = moment().endOf('day').format('X');
    this.excludedTags = [];
    this.includedTags = [];
    this.selectedEthnicities = [];
    this.selectedGroups = [];
    this.selectedLearners = [];
    this.excludedLearners = [];
    this.selectedYearLevels = [];
    this.selectedGenders = [];
    this.selectedATSI = [];
  }

  init() {
    super.init(...arguments);
    this.reset();
  }

  @action
  getChartData(category) {
    return category.tags.map((tag) => {
      let percentage = (tag.count / category.numTags) * 100;
      let tooltip = `${tag.label}<br>${tag.count} posts (${percentage.toFixed(
        1,
      )}%)`;
      return {
        value: tag.id,
        label: tag.label,
        count: tag.count,
        tooltip,
      };
    });
  }

  @action
  openDetailDialog(category, tag) {
    this.detailCategory = category.category;
    this.detailTag = findBy(category.tags, 'id', tag.value);
    this.detailDialogOpen = true;
  }

  @action
  setFilters(string, prop) {
    this.set(string, prop);
  }

  @action
  setDates(dates) {
    this.from = dates.startDate.unix();
    this.to = dates.endDate.unix();
  }
}
